import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { db as firebaseDb, isDemoMode } from '../firebase';
import { useAuth } from './AuthContext';
import type { Progress, User, Qualification, ReactivationRequest, InstructorActivityEntry } from '../types';
import { qualifications as qualificationsData } from '../data/qualifications';
import { definitionService } from '../services/definitionService';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';

interface DataContextType {
  qualifications: Qualification[];
  qualificationsLoading: boolean;
  refreshQualifications: () => Promise<void>;
  allUsers: User[];
  loadingUsers: boolean;
  updateProgress: (userId: string, progress: Progress) => Promise<void>;
  getProgress: (userId: string) => Record<string, Progress>;
  preloadProgress: (userIds: string[]) => Promise<void>;
  updateUserRole: (userId: string, role: string) => Promise<void>;
  createUser: (email: string, role: string) => Promise<User>;
  subscribeProgress: (userId: string, cb: (p: Record<string, Progress>) => void) => () => void;
  seedProgressCache: (userId: string, progress: Record<string, Progress>) => void;
  deactivateUser: (userId: string) => Promise<void>;
  reactivateUser: (userId: string, resetHistory: boolean) => Promise<void>;
  deprecateUser: (userId: string) => Promise<void>;
  getReactivationRequests: () => Promise<ReactivationRequest[]>;
  resolveReactivationRequest: (requestId: string) => Promise<void>;
  getInstructorActivity: (instructorId?: string) => Promise<InstructorActivityEntry[]>;
  refreshUsers: () => Promise<User[]>;
  setExcludeFromProgress: (userId: string, exclude: boolean) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

const demoProgressKey = 'demo_progress';
const demoUsersListKey = 'demo_users_list';

const getDemoProgress = (userId: string): Record<string, Progress> => {
  const key = `${demoProgressKey}_${userId}`;
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      const data = JSON.parse(stored);
      return Object.fromEntries(
        Object.entries(data).map(([key, val]: [string, any]) => [
          key,
          {
            ...val,
            completedAt: val.completedAt ? new Date(val.completedAt) : null,
          },
        ])
      );
    } catch {
      return {};
    }
  }
  return {};
};

const saveDemoProgress = (userId: string, progress: Record<string, Progress>) => {
  const key = `${demoProgressKey}_${userId}`;
  localStorage.setItem(key, JSON.stringify(progress));
};

const getDemoUsersList = (): User[] => {
  const stored = localStorage.getItem(demoUsersListKey);
  if (stored) {
    try {
      return JSON.parse(stored).map((u: any) => ({
        ...u,
        createdAt: new Date(u.createdAt),
        updatedAt: new Date(u.updatedAt),
      }));
    } catch {
      return [];
    }
  }
  return [];
};

const saveDemoUsersList = (users: User[]) => {
  localStorage.setItem(demoUsersListKey, JSON.stringify(users));
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser } = useAuth();
  const [qualifications, setQualifications] = useState<Qualification[]>(qualificationsData);
  const [qualificationsLoading, setQualificationsLoading] = useState(true);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Map of userId -> Set of subscriber callbacks
  const subscribersRef = useRef<Map<string, Set<(p: Record<string, Progress>) => void>>>(new Map());

  // In-memory cache of progress per userId (source of truth for this session)
  const progressCacheRef = useRef<Map<string, Record<string, Progress>>>(new Map());

  useEffect(() => {
    if (isDemoMode) {
      const users = getDemoUsersList();
      setAllUsers(users);
      setLoadingUsers(false);
      return;
    }

    if (!firebaseDb) {
      setLoadingUsers(false);
      return;
    }

    const loadUsers = async () => {
      try {
        const usersRef = collection(firebaseDb!, 'users');
        const snapshot = await getDocs(usersRef);
        const users: User[] = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          users.push({
            uid: doc.id,
            email: data.email,
            displayName: data.displayName,
            photoURL: data.photoURL,
            role: data.role,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            excludeFromProgress: data.excludeFromProgress || false,
            deactivated: data.deactivated || false,
            deactivatedAt: data.deactivatedAt?.toDate() || null,
            deprecated: data.deprecated || false,
            deprecatedAt: data.deprecatedAt?.toDate() || null,
            deprecatedReason: data.deprecatedReason || undefined,
          });
        });

        setAllUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
        setAllUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  // Load qualifications from Firestore (falls back to static data if empty)
  const refreshQualifications = useCallback(async () => {
    try {
      setQualificationsLoading(true);
      const fromService = await definitionService.getQualifications();
      if (fromService.length > 0) {
        setQualifications(fromService);
      }
      // else keep static fallback
    } catch (err) {
      console.error('Error loading qualifications from Firestore:', err);
      // keep static fallback
    } finally {
      setQualificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshQualifications();
  }, [refreshQualifications]);

  const notifySubscribers = useCallback((userId: string, progress: Record<string, Progress>) => {
    const subs = subscribersRef.current.get(userId);
    if (subs) {
      subs.forEach((cb) => cb(progress));
    }
  }, []);

  const getProgress = useCallback((userId: string): Record<string, Progress> => {
    if (isDemoMode) {
      return getDemoProgress(userId);
    }
    return progressCacheRef.current.get(userId) || {};
  }, []);

  const seedProgressCache = useCallback((userId: string, progress: Record<string, Progress>) => {
    progressCacheRef.current.set(userId, { ...progress });
  }, []);

  const preloadProgress = useCallback(async (userIds: string[]) => {
    // Only load users not already cached
    const toLoad = userIds.filter((id) => !progressCacheRef.current.has(id));
    if (toLoad.length === 0) return;

    if (isDemoMode) {
      for (const uid of toLoad) {
        progressCacheRef.current.set(uid, getDemoProgress(uid));
      }
      return;
    }

    if (!firebaseDb) return;

    await Promise.all(
      toLoad.map(async (uid) => {
        try {
          const snapshot = await getDocs(collection(firebaseDb!, 'progress', uid, 'items'));
          const progress: Record<string, Progress> = {};
          snapshot.docs.forEach((d) => {
            const data = d.data();
            progress[d.id] = {
              qualificationId: data.qualificationId,
              itemId: data.itemId,
              category: data.category,
              completed: data.completed,
              completedAt: data.completedAt?.toDate() || null,
              completedBy: data.completedBy || null,
              completedByName: data.completedByName || null,
              notes: data.notes || '',
              grade: data.grade || null,
              rubric: data.rubric || null,
              rubricNotes: data.rubricNotes || null,
            };
          });
          progressCacheRef.current.set(uid, progress);
        } catch (err) {
          console.error(`Failed to preload progress for ${uid}:`, err);
          progressCacheRef.current.set(uid, {});
        }
      })
    );
  }, []);

  const updateProgress = useCallback(async (userId: string, progress: Progress) => {
    const key = `${progress.qualificationId}_${progress.itemId}`;
    console.log('[DataCtx] updateProgress called', { userId, key, completed: progress.completed, completedAt: progress.completedAt, notes: progress.notes?.substring(0, 30) });

    if (isDemoMode) {
      const current = getDemoProgress(userId);
      current[key] = progress;
      saveDemoProgress(userId, current);
      progressCacheRef.current.set(userId, current);
      console.log('[DataCtx] Demo save done, notifying subscribers. Key completed=', current[key]?.completed);
      notifySubscribers(userId, { ...current });
      return;
    }

    if (!firebaseDb) return;

    try {
      const docRef = doc(firebaseDb, 'progress', userId, 'items', key);

      await setDoc(
        docRef,
        {
          qualificationId: progress.qualificationId,
          itemId: progress.itemId,
          category: progress.category,
          completed: progress.completed ?? false,
          completedAt: progress.completedAt ?? null,
          completedBy: progress.completedBy ?? null,
          completedByName: progress.completedByName ?? null,
          notes: progress.notes ?? '',
          grade: progress.grade ?? null,
          rubric: progress.rubric ?? null,
          rubricNotes: progress.rubricNotes ?? null,
        },
        { merge: true }
      );

      // Update local cache and notify subscribers
      const cached = progressCacheRef.current.get(userId) || {};
      cached[key] = progress;
      progressCacheRef.current.set(userId, cached);
      notifySubscribers(userId, { ...cached });
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  }, [notifySubscribers]);

  const subscribeProgress = useCallback((userId: string, cb: (p: Record<string, Progress>) => void) => {
    if (!subscribersRef.current.has(userId)) {
      subscribersRef.current.set(userId, new Set());
    }
    subscribersRef.current.get(userId)!.add(cb);

    // Return unsubscribe function
    return () => {
      const subs = subscribersRef.current.get(userId);
      if (subs) {
        subs.delete(cb);
        if (subs.size === 0) subscribersRef.current.delete(userId);
      }
    };
  }, []);

  const updateUserRole = useCallback(async (userId: string, role: string) => {
    if (isDemoMode) {
      const users = getDemoUsersList();
      const userIndex = users.findIndex((u) => u.uid === userId);
      if (userIndex !== -1) {
        users[userIndex].role = role as any;
        saveDemoUsersList(users);
        setAllUsers(users);
      }
      return;
    }

    if (!firebaseDb) return;

    try {
      await updateDoc(doc(firebaseDb, 'users', userId), {
        role,
        updatedAt: serverTimestamp(),
      });

      const updated = allUsers.map((u) => (u.uid === userId ? { ...u, role: role as any } : u));
      setAllUsers(updated);
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }, [allUsers]);

  const createUser = useCallback(async (email: string, role: string): Promise<User> => {
    const normalizedEmail = email.toLowerCase().trim();

    // Check in-memory first (fast path)
    const existing = allUsers.find((u) => u.email.toLowerCase() === normalizedEmail);
    if (existing) throw new Error('A user with that email already exists');

    const now = new Date();
    const uid = isDemoMode ? `user_${Date.now()}` : crypto.randomUUID();

    const newUser: User = {
      uid,
      email: normalizedEmail,
      displayName: '', // filled in when they log in via Google
      role: role as any,
      createdAt: now,
      updatedAt: now,
    };

    if (isDemoMode) {
      const users = [...getDemoUsersList(), newUser];
      saveDemoUsersList(users);
      setAllUsers(users);
      return newUser;
    }

    if (!firebaseDb) throw new Error('Firebase not configured');

    // Query Firestore directly to prevent duplicates from stale in-memory state
    const dupeQuery = query(
      collection(firebaseDb, 'users'),
      where('email', '==', normalizedEmail)
    );
    const dupeSnap = await getDocs(dupeQuery);
    if (!dupeSnap.empty) {
      throw new Error('A user with that email already exists');
    }

    // In Firestore, use a placeholder doc keyed by a sanitized email
    // When the user logs in, AuthContext will match by email and adopt this doc
    const placeholderId = `pending_${email.replace(/[^a-zA-Z0-9]/g, '_')}`;
    await setDoc(doc(firebaseDb, 'users', placeholderId), {
      email: newUser.email,
      displayName: '',
      role: newUser.role,
      pending: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    newUser.uid = placeholderId;
    setAllUsers((prev) => [...prev, newUser]);
    return newUser;
  }, [allUsers]);

  const refreshUsers = useCallback(async (): Promise<User[]> => {
    if (isDemoMode) {
      const users = getDemoUsersList();
      setAllUsers(users);
      return users;
    }
    if (!firebaseDb) return [];
    const usersRef = collection(firebaseDb, 'users');
    const snapshot = await getDocs(usersRef);
    const users: User[] = [];
    snapshot.docs.forEach((d) => {
      const data = d.data();
      users.push({
        uid: d.id,
        email: data.email,
        displayName: data.displayName,
        photoURL: data.photoURL,
        role: data.role,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        excludeFromProgress: data.excludeFromProgress || false,
        deactivated: data.deactivated || false,
        deactivatedAt: data.deactivatedAt?.toDate() || null,
        deprecated: data.deprecated || false,
        deprecatedAt: data.deprecatedAt?.toDate() || null,
        deprecatedReason: data.deprecatedReason || undefined,
      });
    });
    setAllUsers(users);
    return users;
  }, []);

  // Re-fetch users when auth user changes (e.g. first sign-on creates a new user doc
  // after the initial loadUsers already ran)
  const prevAuthUid = useRef<string | null>(null);
  useEffect(() => {
    if (authUser?.uid && authUser.uid !== prevAuthUid.current) {
      prevAuthUid.current = authUser.uid;
      // If this user isn't in our list yet, refresh
      if (!allUsers.some((u) => u.uid === authUser.uid)) {
        refreshUsers();
      }
    }
  }, [authUser?.uid, allUsers, refreshUsers]);

  const deactivateUser = useCallback(async (userId: string) => {
    const now = new Date();
    if (isDemoMode) {
      const users = getDemoUsersList().map((u) =>
        u.uid === userId ? { ...u, deactivated: true, deactivatedAt: now, updatedAt: now } : u
      );
      saveDemoUsersList(users);
      setAllUsers(users);
      return;
    }
    if (!firebaseDb) return;
    await updateDoc(doc(firebaseDb, 'users', userId), {
      deactivated: true,
      deactivatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    setAllUsers((prev) => prev.map((u) => (u.uid === userId ? { ...u, deactivated: true, deactivatedAt: now } : u)));
  }, []);

  const reactivateUser = useCallback(async (userId: string, resetHistory: boolean) => {
    const now = new Date();
    if (isDemoMode) {
      if (resetHistory) {
        const progress = getDemoProgress(userId);
        if (Object.keys(progress).length > 0) {
          localStorage.setItem(`demo_archived_progress_${userId}_${Date.now()}`, JSON.stringify(progress));
          localStorage.removeItem(`${demoProgressKey}_${userId}`);
          progressCacheRef.current.delete(userId);
        }
      }
      const users = getDemoUsersList().map((u) =>
        u.uid === userId ? { ...u, deactivated: false, deactivatedAt: null, updatedAt: now } : u
      );
      saveDemoUsersList(users);
      setAllUsers(users);
      return;
    }
    if (!firebaseDb) return;
    if (resetHistory) {
      const progressRef = collection(firebaseDb, 'progress', userId, 'items');
      const snap = await getDocs(progressRef);
      const archiveTs = Date.now();
      for (const d of snap.docs) {
        await setDoc(doc(firebaseDb, 'archivedProgress', `${userId}_${archiveTs}`, 'items', d.id), d.data());
        await deleteDoc(d.ref);
      }
      progressCacheRef.current.delete(userId);
    }
    await updateDoc(doc(firebaseDb, 'users', userId), {
      deactivated: false,
      deactivatedAt: null,
      updatedAt: serverTimestamp(),
    });
    setAllUsers((prev) => prev.map((u) => (u.uid === userId ? { ...u, deactivated: false, deactivatedAt: null } : u)));
  }, []);

  const deprecateUser = useCallback(async (userId: string) => {
    const now = new Date();
    if (isDemoMode) {
      const users = getDemoUsersList().map((u) =>
        u.uid === userId ? { ...u, deprecated: true, deprecatedAt: now, deactivated: true, deactivatedAt: u.deactivatedAt || now, deprecatedReason: 'new_employee', updatedAt: now } : u
      );
      saveDemoUsersList(users);
      setAllUsers(users);
      return;
    }
    if (!firebaseDb) return;
    await updateDoc(doc(firebaseDb, 'users', userId), {
      deprecated: true,
      deprecatedAt: serverTimestamp(),
      deactivated: true,
      deprecatedReason: 'new_employee',
      updatedAt: serverTimestamp(),
    });
    setAllUsers((prev) => prev.map((u) =>
      u.uid === userId ? { ...u, deprecated: true, deprecatedAt: now, deactivated: true, deprecatedReason: 'new_employee' } : u
    ));
  }, []);

  const setExcludeFromProgress = useCallback(async (userId: string, exclude: boolean) => {
    const now = new Date();
    if (isDemoMode) {
      const users = getDemoUsersList().map((u) =>
        u.uid === userId ? { ...u, excludeFromProgress: exclude, updatedAt: now } : u
      );
      saveDemoUsersList(users);
      setAllUsers(users);
      return;
    }
    if (!firebaseDb) return;
    await updateDoc(doc(firebaseDb, 'users', userId), {
      excludeFromProgress: exclude,
      updatedAt: serverTimestamp(),
    });
    setAllUsers((prev) => prev.map((u) =>
      u.uid === userId ? { ...u, excludeFromProgress: exclude, updatedAt: now } : u
    ));
  }, []);

  const getReactivationRequests = useCallback(async (): Promise<ReactivationRequest[]> => {
    if (isDemoMode) {
      const stored = localStorage.getItem('demo_reactivation_requests');
      if (!stored) return [];
      try {
        return JSON.parse(stored)
          .filter((r: any) => r.status === 'pending')
          .map((r: any) => ({ ...r, requestedAt: new Date(r.requestedAt) }));
      } catch { return []; }
    }
    if (!firebaseDb) return [];
    const q = query(collection(firebaseDb, 'reactivationRequests'), where('status', '==', 'pending'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        userId: data.userId,
        userEmail: data.userEmail,
        userName: data.userName,
        requestedAt: data.requestedAt?.toDate() || new Date(),
        status: data.status,
      } as ReactivationRequest;
    });
  }, []);

  const resolveReactivationRequest = useCallback(async (requestId: string) => {
    if (isDemoMode) {
      const stored = JSON.parse(localStorage.getItem('demo_reactivation_requests') || '[]');
      const updated = stored.map((r: any) =>
        r.id === requestId ? { ...r, status: 'resolved', resolvedAt: new Date().toISOString() } : r
      );
      localStorage.setItem('demo_reactivation_requests', JSON.stringify(updated));
      return;
    }
    if (!firebaseDb) return;
    await updateDoc(doc(firebaseDb, 'reactivationRequests', requestId), {
      status: 'resolved',
      resolvedAt: serverTimestamp(),
    });
  }, []);

  const getInstructorActivity = useCallback(async (instructorId?: string): Promise<InstructorActivityEntry[]> => {
    const entries: InstructorActivityEntry[] = [];
    const qualMap = new Map(qualifications.map((q) => [q.id, q]));

    if (isDemoMode) {
      for (const u of allUsers) {
        if (u.deprecated) continue;
        const progress = getDemoProgress(u.uid);
        for (const [key, p] of Object.entries(progress)) {
          if (!p.completedBy || !p.completedAt) continue;
          if (instructorId && p.completedBy !== instructorId) continue;
          const qual = qualMap.get(p.qualificationId);
          const item = qual?.items.find((i) => i.id === p.itemId);
          entries.push({
            id: `${u.uid}_${key}`,
            instructorId: p.completedBy,
            instructorName: p.completedByName || 'Unknown',
            studentId: u.uid,
            studentName: u.displayName || u.email,
            qualificationId: p.qualificationId,
            qualificationName: qual?.shortName || p.qualificationId,
            itemId: p.itemId,
            itemTitle: item?.title || p.itemId,
            category: p.category,
            completedAt: new Date(p.completedAt),
            passed: p.completed,
          });
        }
      }
    } else if (firebaseDb) {
      for (const u of allUsers) {
        if (u.deprecated) continue;
        try {
          const progressRef = collection(firebaseDb, 'progress', u.uid, 'items');
          const snap = await getDocs(progressRef);
          for (const d of snap.docs) {
            const p = d.data();
            if (!p.completedBy || !p.completedAt) continue;
            if (instructorId && p.completedBy !== instructorId) continue;
            const qual = qualMap.get(p.qualificationId);
            const item = qual?.items.find((i: any) => i.id === p.itemId);
            entries.push({
              id: `${u.uid}_${d.id}`,
              instructorId: p.completedBy,
              instructorName: p.completedByName || 'Unknown',
              studentId: u.uid,
              studentName: u.displayName || u.email,
              qualificationId: p.qualificationId,
              qualificationName: qual?.shortName || p.qualificationId,
              itemId: p.itemId,
              itemTitle: item?.title || p.itemId,
              category: p.category,
              completedAt: p.completedAt?.toDate() || new Date(),
              passed: p.completed ?? false,
            });
          }
        } catch (err) {
          console.error(`Error loading progress for ${u.uid}:`, err);
        }
      }
    }

    entries.sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime());
    return entries;
  }, [allUsers, qualifications]);

  const value: DataContextType = {
    qualifications,
    qualificationsLoading,
    refreshQualifications,
    allUsers,
    loadingUsers,
    updateProgress,
    getProgress,
    preloadProgress,
    updateUserRole,
    createUser,
    subscribeProgress,
    seedProgressCache,
    deactivateUser,
    reactivateUser,
    deprecateUser,
    getReactivationRequests,
    resolveReactivationRequest,
    getInstructorActivity,
    refreshUsers,
    setExcludeFromProgress,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = (userId?: string) => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within DataProvider');
  }

  const [userProgress, setUserProgress] = useState<Record<string, Progress> | null>(null);
  const [loadingProgress, setProgressLoading] = useState(!!userId);

  // Load initial progress
  useEffect(() => {
    if (!userId) {
      setProgressLoading(false);
      return;
    }

    if (isDemoMode) {
      const progress = getDemoProgress(userId);
      setUserProgress(progress);
      setProgressLoading(false);
      return;
    }

    if (!firebaseDb) {
      setProgressLoading(false);
      return;
    }

    const loadProgress = async () => {
      try {
        const progressRef = collection(firebaseDb!, 'progress', userId, 'items');
        const snapshot = await getDocs(progressRef);
        const progress: Record<string, Progress> = {};

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          progress[doc.id] = {
            qualificationId: data.qualificationId,
            itemId: data.itemId,
            category: data.category,
            completed: data.completed,
            completedAt: data.completedAt?.toDate() || null,
            completedBy: data.completedBy || null,
            completedByName: data.completedByName || null,
            notes: data.notes || '',
            grade: data.grade || null,
            rubric: data.rubric || null,
            rubricNotes: data.rubricNotes || null,
          };
        });

        // Seed the cache so updateProgress merges correctly
        context.seedProgressCache(userId, progress);
        setUserProgress(progress);
      } catch (error) {
        console.error('Error loading progress:', error);
        setUserProgress({});
      } finally {
        setProgressLoading(false);
      }
    };

    loadProgress();
  }, [userId]);

  // Subscribe to progress changes from updateProgress calls
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = context.subscribeProgress(userId, (updated) => {
      const completedKeys = Object.entries(updated).filter(([, v]) => v.completed).map(([k]) => k);
      console.log('[useData] subscriber received update', { userId, totalKeys: Object.keys(updated).length, completedKeys });
      setUserProgress(updated);
    });

    return unsubscribe;
  }, [userId, context.subscribeProgress]);

  return {
    qualifications: context.qualifications,
    qualificationsLoading: context.qualificationsLoading,
    refreshQualifications: context.refreshQualifications,
    allUsers: context.allUsers,
    loadingUsers: context.loadingUsers,
    loadingProgress,
    userProgress,
    updateProgress: context.updateProgress,
    getProgress: context.getProgress,
    preloadProgress: context.preloadProgress,
    updateUserRole: context.updateUserRole,
    createUser: context.createUser,
    deactivateUser: context.deactivateUser,
    reactivateUser: context.reactivateUser,
    deprecateUser: context.deprecateUser,
    getReactivationRequests: context.getReactivationRequests,
    resolveReactivationRequest: context.resolveReactivationRequest,
    getInstructorActivity: context.getInstructorActivity,
    refreshUsers: context.refreshUsers,
    setExcludeFromProgress: context.setExcludeFromProgress,
  };
};
